import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

interface HeroBannerProps {
  title: string;
  subtitle: string;
  imageSrc?: string;
  ctaText?: string;
  ctaLink?: string;
  variant?: 'art' | 'stream' | 'neutral';
}

const HeroBanner: React.FC<HeroBannerProps> = ({ title, subtitle, imageSrc, ctaText, ctaLink, variant = 'neutral' }) => {
  const variantGradients = {
    art: 'from-brand-amber/20 via-brand-purple/20 to-transparent',
    stream: 'from-brand-purple/20 via-brand-indigo/20 to-transparent',
    neutral: 'from-slate-500/20 via-slate-700/20 to-transparent',
  };

  const buttonColors = {
    art: 'bg-brand-amber hover:bg-amber-400 text-slate-900',
    stream: 'bg-brand-purple hover:bg-purple-400 text-white',
    neutral: 'bg-indigo-600 hover:bg-indigo-500 text-white',
  };

  return (
    <div className="relative w-full rounded-3xl overflow-hidden art-card p-0.5 shadow-2xl">
      <div className={`relative w-full h-64 sm:h-80 rounded-[1.4rem] overflow-hidden flex flex-col justify-end p-6 sm:p-10 bg-slate-900 dark:bg-slate-950`}>
        
        {/* Background Image */}
        {imageSrc && (
          <div
            className="absolute inset-0 bg-cover bg-center opacity-40 mix-blend-overlay transition-transform duration-1000 hover:scale-105"
            style={{ backgroundImage: `url(${imageSrc})` }}
          />
        )}
        
        {/* Gradient Overlay */}
        <div className={`absolute inset-0 bg-gradient-to-t ${variantGradients[variant]}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 dark:from-slate-950 via-slate-900/60 dark:via-slate-950/60 to-transparent" />

        {/* Content */}
        <div className="relative z-10 space-y-4 max-w-2xl">
          <div className="space-y-2">
            <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight font-heading leading-tight">
              {title}
            </h1>
            <p className="text-base sm:text-lg text-slate-300 font-medium">
              {subtitle}
            </p>
          </div>

          {ctaText && ctaLink && (
            <div className="pt-2">
              {ctaLink.startsWith('http') ? (
                <a
                  href={ctaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all shadow-lg ${buttonColors[variant]}`}
                >
                  <span>{ctaText}</span>
                  <ArrowRight className="w-4 h-4" />
                </a>
              ) : (
                <Link
                  to={ctaLink}
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all shadow-lg ${buttonColors[variant]}`}
                >
                  <span>{ctaText}</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeroBanner;
